// Generic dashboard section with built-in async state + error isolation
// (Tasks 13.3, 13.4, 13.5).
//
// Each section owns its own async lifecycle so one section's failure never
// suppresses another's data (Req 20.9, 21.7). The section renders a card with
// loading skeletons, an error+retry state, an empty state, or the loaded
// content via UIStateWrapper.

import type { ReactNode } from "react";
import { Card } from "./primitives";
import { UIStateWrapper } from "./UIStateWrapper";
import { useAsyncData } from "../hooks/useAsyncData";

export function AsyncSection<T>({
  title,
  loader,
  emptyText,
  isEmpty,
  action,
  skeletonRows,
  children,
}: {
  title?: string;
  loader: () => Promise<T>;
  emptyText: string;
  isEmpty?: (data: T) => boolean;
  action?: ReactNode;
  skeletonRows?: number;
  children: (data: T) => ReactNode;
}): JSX.Element {
  const { state, reload } = useAsyncData(loader);
  return (
    <Card title={title} action={action}>
      <UIStateWrapper
        state={state}
        emptyText={emptyText}
        isEmpty={isEmpty}
        onRetry={reload}
        skeletonRows={skeletonRows}
      >
        {children}
      </UIStateWrapper>
    </Card>
  );
}
